import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { Loader2, MapPin } from 'lucide-react'

import type { ScriptComment } from '@/api/studio-production'
import { Button } from '@/shared/components/ui/button'
import { ProtoBtn } from '@/features/studio/components/ui-kit'
import { truncateQuote } from '@/features/studio/script/script-comment-anchor'

type ScriptCommentPanelProps = {
  comments: ScriptComment[]
  commentDraft: string
  onCommentDraftChange: (value: string) => void
  onAddComment: () => void | Promise<void>
  onResolveComment?: (comment: ScriptComment) => void | Promise<void>
  onJumpToComment: (comment: ScriptComment) => void
  selectionPreview?: { quote: string; sceneNo?: number } | null
  activeCommentId?: string | null
  showResolve?: boolean
  formatEpisodeLabel?: (episodeNo: number) => string
  total?: number
  loading?: boolean
  loadingMore?: boolean
  hasMore?: boolean
  onLoadMore?: () => void | Promise<void>
}

export function ScriptCommentPanel({
  comments,
  commentDraft,
  onCommentDraftChange,
  onAddComment,
  onResolveComment,
  onJumpToComment,
  selectionPreview,
  activeCommentId,
  showResolve = true,
  formatEpisodeLabel,
  total = 0,
  loading = false,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
}: ScriptCommentPanelProps) {
  const { t } = useTranslation()

  return (
    <div className="space-y-3">
      {selectionPreview ? (
        <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/10 px-3 py-2">
          <p className="text-[10px] font-medium uppercase tracking-wide text-indigo-300/90">
            {t('script.commentSelectionHint', { n: selectionPreview.quote.length })}
          </p>
          <p className="mt-1 line-clamp-3 text-xs text-indigo-100/90">
            「{truncateQuote(selectionPreview.quote, 120)}」
          </p>
          {selectionPreview.sceneNo ? (
            <p className="mt-1 text-[10px] text-indigo-300/70">#{selectionPreview.sceneNo}</p>
          ) : null}
        </div>
      ) : null}

      <textarea
        value={commentDraft}
        onChange={(e) => onCommentDraftChange(e.target.value)}
        rows={3}
        className="w-full rounded-xl border border-[#2a2a3a] bg-[#0f0f17] p-3 text-sm text-zinc-200 outline-none"
        placeholder={t('script.commentPlaceholder')}
      />
      <ProtoBtn size="sm" variant="secondary" className="w-full" onClick={() => void onAddComment()}>
        {t('script.addComment')}
      </ProtoBtn>

      {total > 0 ? (
        <p className="text-[10px] text-zinc-500">
          {t('script.openCommentsCount', { n: total, defaultValue: `${total} 条未解决批注` })}
        </p>
      ) : null}

      {loading && comments.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-6 text-xs text-zinc-500">
          <Loader2 className="h-4 w-4 animate-spin text-indigo-400" />
          {t('script.commentsLoading', { defaultValue: '加载批注…' })}
        </div>
      ) : null}

      {!loading && comments.length === 0 ? (
        <p className="text-xs text-zinc-600">{t('script.commentsEmpty')}</p>
      ) : (
        comments.map((c) => {
          const hasAnchor = c.anchor_start != null && c.anchor_end != null
          const isActive = activeCommentId === c.public_id
          const epLabel = formatEpisodeLabel?.(c.episode_no)
          return (
            <div
              key={c.public_id}
              className={clsx(
                'rounded-xl border bg-[#0e0e16] p-3 transition-colors',
                isActive ? 'border-indigo-500/40 ring-1 ring-indigo-500/20' : 'border-white/[0.06]',
              )}
            >
              {c.anchor_quote ? (
                <button
                  type="button"
                  onClick={() => onJumpToComment(c)}
                  className="mb-2 flex w-full items-start gap-1.5 rounded-lg border border-indigo-500/20 bg-indigo-500/10 px-2 py-1.5 text-left transition hover:border-indigo-500/35 hover:bg-indigo-500/15"
                >
                  <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-indigo-400" />
                  <span className="line-clamp-2 text-[11px] text-indigo-200/90">
                    {truncateQuote(c.anchor_quote, 96)}
                  </span>
                </button>
              ) : hasAnchor || c.scene_no ? (
                <button
                  type="button"
                  onClick={() => onJumpToComment(c)}
                  className="mb-2 inline-flex items-center gap-1 text-[11px] text-indigo-400 hover:text-indigo-300"
                >
                  <MapPin className="h-3 w-3" />
                  {c.scene_no ? `#${c.scene_no}` : t('script.jumpToAnchor')}
                </button>
              ) : null}

              <button type="button" onClick={() => onJumpToComment(c)} className="w-full text-left">
                <p className="text-xs text-zinc-300">{c.body}</p>
              </button>

              <div className="mt-2 flex items-center justify-between text-[10px] text-zinc-600">
                <span>
                  {epLabel ? `${epLabel}` : null}
                  {epLabel && c.scene_no ? ' · ' : null}
                  {c.scene_no ? `#${c.scene_no}` : null}
                  {!epLabel && !c.scene_no ? `#${c.author_id}` : null}
                </span>
                {showResolve && onResolveComment ? (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-indigo-400 hover:text-indigo-300"
                    onClick={() => void onResolveComment(c)}
                  >
                    {t('script.resolveComment')}
                  </Button>
                ) : null}
              </div>
            </div>
          )
        })
      )}

      {hasMore && onLoadMore ? (
        <ProtoBtn
          size="sm"
          variant="secondary"
          className="w-full"
          disabled={loadingMore}
          onClick={() => void onLoadMore()}
        >
          {loadingMore ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {t('script.loadMoreComments', { defaultValue: '加载更多批注' })}
        </ProtoBtn>
      ) : null}
    </div>
  )
}
